import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlaybackResponse, VideoDetail, VideoUpdate } from '@streamr/shared';
import { api } from '../client';
import { routes } from '../routes';
import { getSessionId } from '../../lib/session';

export const useVideo = (id: string) =>
  useQuery({
    queryKey: ['video', id],
    queryFn: ({ signal }) => api.get<VideoDetail>(routes.videos.detail(id), { signal }),
    enabled: id !== '',
  });

/**
 * Polls while a video is still being transcoded so the upload and watch pages
 * flip to the player by themselves. Stops the moment the video is terminal --
 * polling a READY video forever is how a tab quietly generates thousands of
 * requests.
 */
export const useVideoWithStatusPolling = (id: string) =>
  useQuery({
    queryKey: ['video', id],
    queryFn: ({ signal }) => api.get<VideoDetail>(routes.videos.detail(id), { signal }),
    enabled: id !== '',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'UPLOADING' || status === 'PROCESSING' ? 3000 : false;
    },
  });

/**
 * The playback grant: master playlist URL, the first playback token, and where
 * this viewer left off. Never cached across mounts -- the token is short-lived
 * and a stale one would start the session already expired.
 */
export const usePlayback = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: ['playback', id],
    queryFn: ({ signal }) =>
      api.get<PlaybackResponse>(routes.videos.playback(id), {
        query: { sessionId: getSessionId() },
        signal,
      }),
    enabled: enabled && id !== '',
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

export const useUpdateVideo = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: VideoUpdate) => api.patch<VideoDetail>(routes.videos.detail(id), update),
    onSuccess: (updated) => {
      queryClient.setQueryData(['video', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
      void queryClient.invalidateQueries({ queryKey: ['channel-videos'] });
    },
  });
};

export const useDeleteVideo = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>(routes.videos.detail(id)),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['video', id] });
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
      void queryClient.invalidateQueries({ queryKey: ['channel-videos'] });
    },
  });
};

/** Fire-and-forget: a failed view ping must never interrupt playback. */
export const recordView = (videoId: string, positionSec: number): void => {
  void api
    .post(routes.videos.view(videoId), { sessionId: getSessionId(), positionSec })
    .catch(() => undefined);
};

export const saveProgress = (videoId: string, positionSec: number): void => {
  void api.put(routes.videos.progress(videoId), { positionSec }).catch(() => undefined);
};
